'use client'

import type { Editor } from '@tiptap/core'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FloatingMenu as TipTapFloatingMenu } from '@tiptap/react/menus'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ChevronRight,
  Code2,
  CodeSquare,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Minus,
  Quote,
  TextQuote
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { ScrollArea } from '@/components/ui/scroll-area'

import { useDebounce } from '@/hooks/use-debounce'

import { cn } from '@/utils'

type CommandGroupType = {
  groupKey: string
  items: CommandItemType[]
}

interface CommandItemType {
  command: (editor: Editor) => void
  descriptionKey: string
  icon: React.ComponentType<{ className?: string }>
  id: string
  keywords: string
  titleKey: string
}

const hasEditorExtension = (editor: Editor, extensionName: string) =>
  editor.extensionManager.extensions.some(
    (extension) => extension.name === extensionName
  )

const hasEditorCommand = (editor: Editor, commandName: string) =>
  typeof (editor.commands as Record<string, unknown>)[commandName] ===
  'function'

const resolveEditorElement = (
  element: Editor['options']['element']
): HTMLElement | null => {
  if (!element) {
    return null
  }

  if (element instanceof HTMLElement) {
    return element
  }

  if (
    typeof element === 'object' &&
    'mount' in element &&
    element.mount instanceof HTMLElement
  ) {
    return element.mount
  }

  return null
}

const isItemAvailable = (editor: Editor, itemId: string): boolean => {
  switch (itemId) {
    case 'alignCenter':
    case 'alignLeft':
    case 'alignRight':
      return (
        hasEditorExtension(editor, 'textAlign') &&
        hasEditorCommand(editor, 'setTextAlign')
      )
    case 'blockquote':
    case 'quote':
      return (
        hasEditorExtension(editor, 'blockquote') &&
        hasEditorCommand(editor, 'toggleBlockquote')
      )
    case 'bulletList':
      return (
        hasEditorExtension(editor, 'bulletList') &&
        hasEditorCommand(editor, 'toggleBulletList')
      )
    case 'code':
      return (
        hasEditorExtension(editor, 'code') &&
        hasEditorCommand(editor, 'toggleCode')
      )
    case 'codeBlock':
      return (
        hasEditorExtension(editor, 'codeBlock') &&
        hasEditorCommand(editor, 'toggleCodeBlock')
      )
    case 'heading1':
    case 'heading2':
    case 'heading3':
      return (
        hasEditorExtension(editor, 'heading') &&
        hasEditorCommand(editor, 'toggleHeading')
      )
    case 'horizontalRule':
      return (
        hasEditorExtension(editor, 'horizontalRule') &&
        hasEditorCommand(editor, 'setHorizontalRule')
      )
    case 'image':
      return hasEditorCommand(editor, 'insertImagePlaceholder')
    case 'numberedList':
      return (
        hasEditorExtension(editor, 'orderedList') &&
        hasEditorCommand(editor, 'toggleOrderedList')
      )
    default:
      return true
  }
}

const groups: CommandGroupType[] = [
  {
    groupKey: 'basicBlocks',
    items: [
      {
        command: (editor) => editor.chain().focus().clearNodes().run(),
        descriptionKey: 'text',
        icon: ChevronRight,
        id: 'text',
        keywords: 'paragraph text',
        titleKey: 'text'
      },
      {
        command: (editor) =>
          editor.chain().focus().toggleHeading({ level: 1 }).run(),
        descriptionKey: 'heading1',
        icon: Heading1,
        id: 'heading1',
        keywords: 'h1 title header',
        titleKey: 'heading1'
      },
      {
        command: (editor) =>
          editor.chain().focus().toggleHeading({ level: 2 }).run(),
        descriptionKey: 'heading2',
        icon: Heading2,
        id: 'heading2',
        keywords: 'h2 subtitle',
        titleKey: 'heading2'
      },
      {
        command: (editor) =>
          editor.chain().focus().toggleHeading({ level: 3 }).run(),
        descriptionKey: 'heading3',
        icon: Heading3,
        id: 'heading3',
        keywords: 'h3 subheader',
        titleKey: 'heading3'
      },
      {
        command: (editor) => editor.chain().focus().toggleBulletList().run(),
        descriptionKey: 'bulletList',
        icon: List,
        id: 'bulletList',
        keywords: 'unordered ul bullets',
        titleKey: 'bulletList'
      },
      {
        command: (editor) => editor.chain().focus().toggleOrderedList().run(),
        descriptionKey: 'numberedList',
        icon: ListOrdered,
        id: 'numberedList',
        keywords: 'numbered ol',
        titleKey: 'numberedList'
      },
      {
        command: (editor) => editor.chain().focus().toggleCodeBlock().run(),
        descriptionKey: 'codeBlock',
        icon: Code2,
        id: 'codeBlock',
        keywords: 'code snippet pre',
        titleKey: 'codeBlock'
      },

      {
        command: (editor) => editor.chain().focus().setHorizontalRule().run(),
        descriptionKey: 'horizontalRule',
        icon: Minus,
        id: 'horizontalRule',
        keywords: 'horizontal rule divider',
        titleKey: 'horizontalRule'
      }
    ]
  },
  {
    groupKey: 'inline',
    items: [
      {
        command: (editor) => editor.chain().focus().toggleBlockquote().run(),
        descriptionKey: 'quote',
        icon: Quote,
        id: 'quote',
        keywords: 'blockquote cite',
        titleKey: 'quote'
      },
      {
        command: (editor) => editor.chain().focus().toggleCode().run(),
        descriptionKey: 'code',
        icon: CodeSquare,
        id: 'code',
        keywords: 'code inline',
        titleKey: 'code'
      },
      {
        command: (editor) => editor.chain().focus().toggleBlockquote().run(),
        descriptionKey: 'blockquote',
        icon: TextQuote,
        id: 'blockquote',
        keywords: 'blockquote quote',
        titleKey: 'blockquote'
      }
    ]
  },
  {
    groupKey: 'alignment',
    items: [
      {
        command: (editor) => editor.chain().focus().setTextAlign('left').run(),
        descriptionKey: 'alignLeft',
        icon: AlignLeft,
        id: 'alignLeft',
        keywords: 'align left',
        titleKey: 'alignLeft'
      },
      {
        command: (editor) =>
          editor.chain().focus().setTextAlign('center').run(),
        descriptionKey: 'alignCenter',
        icon: AlignCenter,
        id: 'alignCenter',
        keywords: 'align center',
        titleKey: 'alignCenter'
      },
      {
        command: (editor) => editor.chain().focus().setTextAlign('right').run(),
        descriptionKey: 'alignRight',
        icon: AlignRight,
        id: 'alignRight',
        keywords: 'align right',
        titleKey: 'alignRight'
      }
    ]
  }
]

export function FloatingMenu({ editor }: { editor: Editor }) {
  const t = useTranslations('components.tiptap.floatingMenu')
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const commandRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  const translatedGroups = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        group: t(`groups.${group.groupKey}` as never),
        items: group.items.map((item) => ({
          ...item,
          description: t(`items.${item.descriptionKey}.description` as never),
          title: t(`items.${item.titleKey}.title` as never)
        }))
      })),
    [t]
  )

  const filteredGroups = useMemo(
    () =>
      translatedGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              isItemAvailable(editor, item.id) &&
              (item.title
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase()) ||
                item.description
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase()) ||
                item.keywords
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase()))
          )
        }))
        .filter((group) => group.items.length > 0),
    [debouncedSearch, editor, translatedGroups]
  )

  const flatFilteredItems = useMemo(
    () => filteredGroups.flatMap((g) => g.items),
    [filteredGroups]
  )

  const executeCommand = useCallback(
    (commandFn: (editor: Editor) => void) => {
      if (!editor) return

      try {
        const { from } = editor.state.selection
        const slashCommandLength = search.length + 1

        editor
          .chain()
          .focus()
          .deleteRange({
            from: Math.max(0, from - slashCommandLength),
            to: from
          })
          .run()

        commandFn(editor)
      } catch (error) {
        console.error('Error executing command:', error)
      } finally {
        setIsOpen(false)
        setSearch('')
        setSelectedIndex(-1)
      }
    },
    [editor, search]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || !editor) return

      const preventDefault = () => {
        e.preventDefault()
        e.stopImmediatePropagation()
      }

      switch (e.key) {
        case 'ArrowDown':
          preventDefault()
          setSelectedIndex((prev) => {
            if (prev === -1) return 0
            return prev < flatFilteredItems.length - 1 ? prev + 1 : 0
          })
          break

        case 'ArrowUp':
          preventDefault()
          setSelectedIndex((prev) => {
            if (prev === -1) return flatFilteredItems.length - 1
            return prev > 0 ? prev - 1 : flatFilteredItems.length - 1
          })
          break

        case 'Enter':
          preventDefault()
          const targetIndex = selectedIndex === -1 ? 0 : selectedIndex
          if (flatFilteredItems[targetIndex]) {
            executeCommand(flatFilteredItems[targetIndex].command)
          }
          break

        case 'Escape':
          preventDefault()
          setIsOpen(false)
          setSelectedIndex(-1)
          break
      }
    },
    [isOpen, selectedIndex, flatFilteredItems, executeCommand, editor]
  )

  useEffect(() => {
    const editorElement = resolveEditorElement(editor?.options.element)
    if (!editorElement) return

    const handleEditorKeyDown = (e: Event) => handleKeyDown(e as KeyboardEvent)

    editorElement.addEventListener('keydown', handleEditorKeyDown)
    return () =>
      editorElement.removeEventListener('keydown', handleEditorKeyDown)
  }, [handleKeyDown, editor])

  const [prevSearch, setPrevSearch] = useState(search)
  if (prevSearch !== search) {
    setPrevSearch(search)
    setSelectedIndex(-1)
  }

  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.focus()
    }
  }, [selectedIndex])

  return (
    <TipTapFloatingMenu
      appendTo={() => document.body}
      editor={editor}
      options={{
        onHide: () => {
          setIsOpen(false)
          setSelectedIndex(-1)
        },
        placement: 'bottom-start'
      }}
      shouldShow={({ state }) => {
        if (!editor) return false

        const { $from } = state.selection
        const currentLineText = $from.parent.textBetween(
          0,
          $from.parentOffset,
          '\n',
          ' '
        )

        const isSlashCommand =
          currentLineText.startsWith('/') &&
          $from.parent.type.name !== 'codeBlock' &&
          $from.parentOffset === currentLineText.length

        if (!isSlashCommand) {
          if (isOpen) setIsOpen(false)
          return false
        }

        const query = currentLineText.slice(1).trim()
        if (query !== search) setSearch(query)
        if (!isOpen) setIsOpen(true)
        return true
      }}
    >
      <Command
        className="bg-popover z-50 w-72 overflow-hidden rounded-lg border shadow-lg"
        ref={commandRef}
        role="listbox"
      >
        <ScrollArea className="max-h-[330px]">
          <CommandList>
            <CommandEmpty className="text-muted-foreground py-3 text-center text-sm">
              {t('noResultsFound')}
            </CommandEmpty>

            {filteredGroups.map((group, groupIndex) => (
              <CommandGroup
                heading={
                  <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                    {group.group}
                  </div>
                }
                key={`${group.groupKey}-${groupIndex}`}
              >
                {group.items.map((item, itemIndex) => {
                  const flatIndex =
                    filteredGroups
                      .slice(0, groupIndex)
                      .reduce((acc, g) => acc + g.items.length, 0) + itemIndex

                  return (
                    <CommandItem
                      aria-selected={flatIndex === selectedIndex}
                      className={cn(
                        'aria-selected:bg-accent/50 gap-3',
                        flatIndex === selectedIndex ? 'bg-accent/50' : ''
                      )}
                      key={`${group.groupKey}-${item.id}-${itemIndex}`}
                      onSelect={() => executeCommand(item.command)}
                      ref={(el) => {
                        itemRefs.current[flatIndex] = el
                      }}
                      role="option"
                      tabIndex={flatIndex === selectedIndex ? 0 : -1}
                      value={`${group.groupKey}-${item.id}`}
                    >
                      <div className="bg-background flex h-9 w-9 items-center justify-center rounded-md border">
                        <item.icon className="h-4 w-4" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <span className="text-sm font-medium">
                          {item.title}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {item.description}
                        </span>
                      </div>
                      <kbd className="bg-muted text-muted-foreground ml-auto flex h-5 items-center rounded px-1.5 text-xs">
                        ↵
                      </kbd>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </ScrollArea>
      </Command>
    </TipTapFloatingMenu>
  )
}
